<template>
  <v-navigation-drawer
    class="h-100 py-10 px-12 d-flex flex-column"
    fixed
    overlay-opacity="0.7"
    right
    temporary
    width="450"
    :permanent="showRecognitionDialog"
    :value="visible"
    @input="handleInput"
  >
    <div v-if="account">
      <div class="d-flex flex-column align-center justify-center">
        <UserAvatar class="mb-4" :account="account" data-private />

        <p
          class="mx-0 mt-0 mb-1 pa-0 headline text-center text-truncate"
          data-private
        >
          {{ account.nickname || getFullName(account) }}
        </p>

        <p
          v-if="account.position"
          class="body-1 blue-grey--text text--darken-1 text-center ma-0 pa-0"
        >
          {{ account.position }}
        </p>

        <UserRoleChips class="mt-4 justify-center" :account="account" />
        <div class="d-flex justify-center mt-4">
          <Button
            color="primary"
            :text="$t('common.users.view_profile')"
            :to="{
              name: 'profile',
              params: {
                organizationId: organizationId,
                userId: account._id,
              },
            }"
            @click="
              trackDirectoryNavigate({
                section: 'userDetailsDrawer',
                element: 'goToProfileButton',
              })
            "
          />

          <Button
            outlined
            color="primary"
            class="ms-3"
            data-testid="userDetailsDrawer-button-recognize"
            v-if="!isSelfProfile"
            :text="$t('recognitions.recognize')"
            @click="openRecognitionDialog"
          />
        </div>
      </div>
      <div class="d-flex flex-column">
        <p class="mt-6 mb-3 subtitle-2" style="font-weight: 700">
          {{ $t('common.users.personal_info') }}
        </p>
        <div
          class="d-flex mb-4 align-center"
          v-if="account.displayname || account.familyname"
        >
          <v-icon size="24" class="blue-grey--text me-4"
            >mdi-account-outline</v-icon
          >
          <p class="ma-0 pa-0 body-2">{{ getFullName(account) }}</p>
        </div>
        <div class="d-flex align-center" v-if="account.email">
          <v-icon size="24" class="blue-grey--text me-4"
            >mdi-email-outline
          </v-icon>
          <p class="ma-0 pa-0 body-2" data-private>{{ account.email }}</p>
        </div>

        <p
          class="mt-5 subtitle-2 mb-n1"
          style="font-weight: 700"
          data-testid="userDetailsDrawer-text-recognitionsTitle"
        >
          {{ $t('recognitions.recognition_other') }}
        </p>
        <div class="mt-4 d-flex align-center">
          <div class="d-flex me-14 align-center">
            <v-icon
              size="24"
              class="blue-grey--text me-4"
              data-testid="userDetailsDrawer-icon-recognitions"
              >mdi-medal-outline
            </v-icon>
            <p
              class="ma-0 pa-0 body-2"
              data-private
              data-testid="userDetailsDrawer-text-receivedRecognitions"
            >
              {{ $t('recognitions.received') }} {{ recognitions.received }}
            </p>
          </div>
          <div class="d-flex align-center">
            <v-icon size="24" class="blue-grey--text me-4"
              >mdi-medal-outline
            </v-icon>
            <p
              class="ma-0 pa-0 body-2"
              data-private
              data-testid="userDetailsDrawer-text-givenRecognitions"
            >
              {{ $t('recognitions.given') }} {{ recognitions.given }}
            </p>
          </div>
        </div>
        <p
          class="mt-5 subtitle-2 mb-n1"
          style="font-weight: 700"
          v-if="account.teams.length"
        >
          {{ $t('common.team_other') }}
        </p>
        <div
          class="d-flex mt-4 align-center"
          v-for="team in account.teams"
          :key="team.id"
        >
          <v-icon size="24" class="blue-grey--text me-4 align-self-start"
            >mdi-account-group-outline
          </v-icon>
          <p class="ma-0 pa-0 body-2">{{ team.name }}</p>
        </div>
        <p class="mt-5 mb-2 subtitle-2" style="font-weight: 700">
          {{ $t('common.location') }}
        </p>
        <div class="d-flex flex-wrap">
          <v-chip outlined class="me-2 mb-2">{{ account.site.name }}</v-chip>
          <v-chip
            outlined
            v-for="tag in account.tags"
            class="me-2 mb-2"
            :key="tag.id"
            >{{ tag.name }}</v-chip
          >
        </div>
        <p
          class="mt-5 mb-2 subtitle-2"
          style="font-weight: 700"
          v-if="account.skills.length"
        >
          {{ $t('profile_settings.skill_other') }}
        </p>
        <div class="d-flex flex-wrap">
          <v-chip
            outlined
            v-for="skill in account.skills"
            class="me-2 mb-2"
            :key="skill"
            >{{ skill }}</v-chip
          >
        </div>
      </div>
    </div>
    <RecognitionDialog
      v-if="account"
      :show="showRecognitionDialog"
      :initialAccounts="[account._id]"
      @saved="handleSendRecognition"
      @close="showRecognitionDialog = false"
    />
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '@/api'

import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import userMixin from '@/mixins/userMixin.vue'
import mixin from '@/mixins/mixin.vue'

import Button from '@/components/UI/Button.vue'
import UserRoleChips from '@/components/UserRoleChips.vue'
import UserAvatar from '@/components/UserAvatar.vue'
import RecognitionDialog from '@/components/RecognitionDialog.vue'

export default {
  name: 'UserDetailsDrawer',

  mixins: [userMixin, trackerMixin, mixin],

  components: {
    Button,
    UserAvatar,
    UserRoleChips,
    RecognitionDialog,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    account: {
      type: Object,
      required: false,
    },
  },

  data() {
    return {
      showRecognitionDialog: false,
      recognitions: { given: 0, received: 0 },
      isLoading: false,
    }
  },

  watch: {
    account(newValue, oldValue) {
      if (newValue !== oldValue && newValue?._id) {
        return this.getRecognitionsSummary()
      }
    },
  },

  computed: {
    ...mapGetters(['organizationId', 'accountId']),

    isSelfProfile() {
      return Boolean(this.accountId === this.account._id)
    },
  },

  methods: {
    handleSendRecognition() {
      this.showRecognitionDialog = false
      this.getRecognitionsSummary()
    },

    async getRecognitionsSummary() {
      try {
        this.isLoading = true
        const response = await api.recognition.getAccountSummary(
          this.organizationId,
          this.account._id
        )

        this.recognitions = response.data
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    handleInput(visible) {
      if (!visible) this.$emit('close')
    },

    openRecognitionDialog() {
      this.trackRecognitionsNavigate({
        option: 'giveRecognition',
        section: 'directory',
      })
      this.showRecognitionDialog = true
    },
  },
}
</script>
